import Phaser from 'phaser';
import { Projectile } from './Projectile';
import { getGameContext } from '../utils/context';
import { SFX } from '../audio/AudioKeys';

// 8-direction enum used for movement/facing state
type Dir8 = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW';

// Keep legacy Facing for compatibility with existing logic/comments
type Facing = 'up' | 'down' | 'left' | 'right';

/**
 * Changes:
 * - Upgraded to 8-direction movement/facing (N, NE, E, SE, S, SW, W, NW) with rotation-only visualization.
 * - Uses a single base animation "player-walk_side" for all moving states and "player-idle_side" for idle.
 * - Controls sprite.angle to visually convey direction (no flip mirroring); rotation can be toggled off if needed.
 * - Watermeter logic is scene-authoritative (Task 2.2). Player no longer owns timers or water state.
 */
export class Player extends Phaser.Physics.Arcade.Sprite {
    private cursors: Phaser.Types.Input.Keyboard.CursorKeys;
    private wasd: { W: Phaser.Input.Keyboard.Key, A: Phaser.Input.Keyboard.Key, S: Phaser.Input.Keyboard.Key, D: Phaser.Input.Keyboard.Key };
    private fireKey!: Phaser.Input.Keyboard.Key;
    // Movement speed configuration. Task 2.4: drastically reduce when water is depleted.
    private readonly baseSpeed: number = 200;
    private readonly depletedSpeed: number = 60; // significant slowdown when water is 0

    // Config: allow rotation of sprite to indicate direction. Set to false to keep angle at 0.
    private allowRotation: boolean = true;

    // Remember last 8-direction to use for idle pose
    private lastDir8: Dir8 = 'S';

    // Pistol power-up state (Task 5.1)
    public hasPistol: boolean = false;
    public shotsRemaining: number = 0;

    /**
     * Grant the pistol to the player and optionally set the number of shots.
     * Plays pickup SFX if loaded.
     */
    public grantPistol(shots: number = 6): void {
        this.hasPistol = true;
        this.shotsRemaining = Math.max(0, shots | 0);
        try {
            const { sound } = getGameContext(this.scene);
            const canPlay = !!this.scene?.cache?.audio?.has?.(SFX.Pickup);
            if (canPlay) {
                sound?.playSfx(SFX.Pickup, { volume: 0.9 }, 'ui_click');
            }
        } catch {
            // ignore if context not ready
        }
    }

    constructor(scene: Phaser.Scene, x: number, y: number) {
        super(scene, x, y, 'player');

        scene.add.existing(this);
        scene.physics.add.existing(this);

        this.setCollideWorldBounds(true);
        this.setDepth(5);

        // Configure a tighter Arcade Physics body to collide reliably with 32x32 tiles.
        // Kenney character frames are larger than a single tile; use a smaller centered hitbox.
        const body = this.body as Phaser.Physics.Arcade.Body;
        const bw = 18;
        const bh = 18;
        // Ensure body is set after physics add.existing
        this.setSize(bw, bh);
        // Center the body within the sprite texture/frame
        this.setOffset((this.width - bw) / 2, (this.height - bh) / 2);
        // Optional slight drag for smoother stopping near walls
        body.setDrag(600, 600);

        const kb = scene.input.keyboard!;
        this.cursors = kb.createCursorKeys();
        this.wasd = {
            W: kb.addKey(Phaser.Input.Keyboard.KeyCodes.W),
            A: kb.addKey(Phaser.Input.Keyboard.KeyCodes.A),
            S: kb.addKey(Phaser.Input.Keyboard.KeyCodes.S),
            D: kb.addKey(Phaser.Input.Keyboard.KeyCodes.D),
        };
        // Fire key: SPACE
        this.fireKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

        // Initial idle pose
        this.play('player-idle_side');
        this.setAngle(0);
        this.setFlipX(false);
    }

    /**
     * Expose current water seconds by reading the scene-authoritative value.
     * This keeps existing calls (e.g., GameScene HUD) working without duplicating state.
     */
    public getWater(): number {
        const sceneAny = this.scene as any;
        const v = sceneAny?.currentWaterSeconds;
        return typeof v === 'number' ? v : 0;
    }

    // Resolve dx,dy into one of 8 directions using 22.5° sector boundaries.
    // Angle is degrees from Math.atan2(dy, dx): 0° = +X (E), +90° = +Y (S), -90° = -Y (N).
    // Sectors (centered on E, NE, N, NW, W, SW, S, SE), boundaries at ±22.5°, 67.5°, 112.5°, 157.5°:
    //   (-22.5, 22.5]      => E
    //   (22.5, 67.5]       => SE
    //   (67.5, 112.5]      => S
    //   (112.5, 157.5]     => SW
    //   (157.5, -157.5]    => W
    //   (-157.5, -112.5]   => NW
    //   (-112.5, -67.5]    => N
    //   (-67.5, -22.5]     => NE
    private getDirection8(dx: number, dy: number): Dir8 {
        const ang = Phaser.Math.RadToDeg(Math.atan2(dy, dx)); // -180..180
        if (ang > -22.5 && ang <= 22.5) return 'E';
        if (ang > 22.5 && ang <= 67.5) return 'SE';
        if (ang > 67.5 && ang <= 112.5) return 'S';
        if (ang > 112.5 && ang <= 157.5) return 'SW';
        if (ang > 157.5 || ang <= -157.5) return 'W';
        if (ang > -157.5 && ang <= -112.5) return 'NW';
        if (ang > -112.5 && ang <= -67.5) return 'N';
        // if (ang > -67.5 && ang <= -22.5)
        return 'NE';
    }

    // Apply angle and animation based on moving/idle. For clarity, avoid flipX and use rotation only.
    private playDirection(moving: boolean, dir: Dir8) {
        // Always keep flipX false (rotation conveys left/right).
        this.setFlipX(false);

        if (!this.allowRotation) {
            this.setAngle(0);
        } else {
            let angle = 0;
            switch (dir) {
                case 'E': angle = 0; break;
                case 'NE': angle = -45; break;
                case 'N': angle = -90; break;
                case 'NW': angle = -135; break;
                case 'W': angle = 180; break;
                case 'SW': angle = 135; break;
                case 'S': angle = 90; break;
                case 'SE': angle = 45; break;
            }
            this.setAngle(angle);
        }

        const desired = moving ? 'player-walk_side' : 'player-idle_side';
        if (this.anims.currentAnim?.key !== desired || !this.anims.isPlaying) {
            this.play(desired, true);
        }
    }

    update() {
        // Reset velocity first
        this.setVelocity(0);

        // Input axes
        const left = this.cursors.left.isDown || this.wasd.A.isDown;
        const right = this.cursors.right.isDown || this.wasd.D.isDown;
        const up = this.cursors.up.isDown || this.wasd.W.isDown;
        const down = this.cursors.down.isDown || this.wasd.S.isDown;

        // Build raw vector
        let dx = 0;
        let dy = 0;
        if (left) dx -= 1;
        if (right) dx += 1;
        if (up) dy -= 1;
        if (down) dy += 1;

        // Determine effective speed based on water status from scene (scene-authoritative)
        const sceneAny = this.scene as any;
        const waterLeft: number = typeof sceneAny?.currentWaterSeconds === 'number' ? sceneAny.currentWaterSeconds : 0;
        const effectiveSpeed = waterLeft > 0 ? this.baseSpeed : this.depletedSpeed;
 
        // Apply physics velocity using effective speed
        if (dx !== 0) {
            this.setVelocityX(dx * effectiveSpeed);
        }
        if (dy !== 0) {
            this.setVelocityY(dy * effectiveSpeed);
        }
 
        // Normalize diagonal speed at effective speed
        (this.body as Phaser.Physics.Arcade.Body).velocity.normalize().scale(effectiveSpeed);

        const mag = Math.hypot(dx, dy);
        const moving = mag > 0.0001;

        if (moving) {
            // Resolve to 8 directions using vector angle
            const dir = this.getDirection8(dx, dy);
            // Persist last facing
            this.lastDir8 = dir;
            this.playDirection(true, dir);
        } else {
            // Idle: hold last direction orientation and show idle
            const dir: Dir8 = this.lastDir8;
            this.playDirection(false, dir);
        }
 
        // Shooting input: once per key press
        if (Phaser.Input.Keyboard.JustDown(this.fireKey)) {
            this.tryShoot();
        }
    }

    // Task 5.3: Shooting Mechanics — create and launch a projectile if allowed
    private tryShoot() {
        if (!this.hasPistol) return;
        if (this.shotsRemaining <= 0) return;

        // Determine direction vector based on facing (true 8-way)
        let vx = 0;
        let vy = 0;
        switch (this.lastDir8) {
            case 'N':  vx = 0;  vy = -1; break;
            case 'NE': vx = 1;  vy = -1; break;
            case 'E':  vx = 1;  vy = 0;  break;
            case 'SE': vx = 1;  vy = 1;  break;
            case 'S':  vx = 0;  vy = 1;  break;
            case 'SW': vx = -1; vy = 1;  break;
            case 'W':  vx = -1; vy = 0;  break;
            case 'NW': vx = -1; vy = -1; break;
        }

        const mag = Math.hypot(vx, vy) || 1;
        vx /= mag; vy /= mag;

        const bulletSpeed = 400;
        const spawnOffset = 12;
        const spawnX = this.x + vx * spawnOffset;
        const spawnY = this.y + vy * spawnOffset;

        // Obtain the scene-scoped projectiles group via typed GameContext (no casting/importing GameScene)
        let projectilesGroup: Phaser.Physics.Arcade.Group;
        try {
            const { projectiles, sound } = getGameContext(this.scene);
            projectilesGroup = projectiles;

            // Play shoot SFX with rate limit bucket 'shoot'
            // Ensure the loader actually cached the key before attempting to play.
            const canPlay = !!this.scene?.cache?.audio?.has?.(SFX.Shoot);
            if (canPlay) {
                sound?.playSfx(SFX.Shoot, { volume: 0.9 }, 'shoot');
            } else {
                // Dev hint during setup when assets are minimal (avoid Node-only globals)
                // eslint-disable-next-line no-console
                console.debug('[Audio] Shoot SFX not loaded; skipping play');
            }
        } catch (e) {
            // Fallback: construct directly if context not yet initialized
            const fallback = new Projectile(this.scene, spawnX, spawnY, 'bullet').setMeta(this, 1);
            fallback.initVelocity(vx, vy, bulletSpeed);
            this.shotsRemaining = Math.max(0, this.shotsRemaining - 1);
            return;
        }

        // Retrieve an inactive projectile or create a new one via the group's factory
        const proj = projectilesGroup.get(spawnX, spawnY, 'bullet') as Projectile | Phaser.GameObjects.GameObject | null;

        if (!proj) {
            // Pool capacity reached (when maxSize is set) — skip firing without consuming ammo
            return;
        }

        // Ensure correct type and activation/visibility
        const projectile = proj as Projectile;
        projectile.setActive(true).setVisible(true);

        // Some Phaser versions need body re-enabled upon revive
        const body = projectile.body as Phaser.Physics.Arcade.Body | undefined;
        if (body) {
            body.enable = true;
            // Ensure pooled defaults match group settings
            body.setAllowGravity(false);
            body.setImmovable(false);
            body.setDrag(0, 0);
            body.setDamping(false);
        }

        // Attach shooter metadata and launch AFTER activation
        projectile.setMeta(this, 1);
        projectile.initVelocity(vx, vy, bulletSpeed);

        // Consume ammo
        this.shotsRemaining = Math.max(0, this.shotsRemaining - 1);
    }
}
